"use client";

import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { 
  FileText, 
  Download, 
  CheckCircle, 
  Clock, 
  AlertCircle,
  Signature,
  Eye
} from "lucide-react";
import { format } from "date-fns";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/utils/supabase/client";
import { useQuery } from "@tanstack/react-query";
import { showSuccess, showError } from "@/utils/toast";
import { SignaturePad } from "./SignaturePad"; // Changed from default to named import

interface ClientSignedDocsProps {}

const ClientSignedDocs: React.FC<ClientSignedDocsProps> = () => {
  const { profile } = useAuth();
  const [showSignature, setShowSignature] = useState(false);
  const [documentToSign, setDocumentToSign] = useState<any>(null);
  const [documentType, setDocumentType] = useState<'invoice' | 'quote'>('invoice');

  // Fetch signed documents
  const { data: signedDocs, isLoading, error } = useQuery({
    queryKey: ['client-signed-docs', profile?.id],
    queryFn: async () => {
      if (!profile?.id) return [];
      
      const [invoicesRes, quotesRes] = await Promise.all([
        supabase
          .from('invoices')
          .select('*, clients(name)')
          .eq('client_id', profile.id)
          .not('signature_url', 'is', null)
          .order('signed_at', { ascending: false }),
        supabase
          .from('quotes')
          .select('*, clients(name)')
          .eq('client_id', profile.id)
          .not('signature_url', 'is', null)
          .order('signed_at', { ascending: false }),
      ]);

      const invoices = (invoicesRes.data || []).map(inv => ({
        ...inv,
        type: 'invoice',
        document_number: inv.invoice_number,
      }));
      
      const quotes = (quotesRes.data || []).map(quote => ({
        ...quote,
        type: 'quote',
        document_number: quote.quote_number,
      }));

      return [...invoices, ...quotes];
    },
    enabled: !!profile?.id,
  });

  // Fetch pending documents (unsigned)
  const { data: pendingDocs } = useQuery({
    queryKey: ['client-pending-docs', profile?.id],
    queryFn: async () => {
      if (!profile?.id) return [];
      
      const [invoicesRes, quotesRes] = await Promise.all([
        supabase
          .from('invoices')
          .select('*, clients(name)')
          .eq('client_id', profile.id)
          .eq('status', 'sent')
          .is('signature_url', null)
          .order('created_at', { ascending: false }),
        supabase
          .from('quotes')
          .select('*, clients(name)')
          .eq('client_id', profile.id)
          .eq('status', 'sent')
          .is('signature_url', null)
          .order('created_at', { ascending: false }),
      ]);

      const invoices = (invoicesRes.data || []).map(inv => ({
        ...inv,
        type: 'invoice',
        document_number: inv.invoice_number,
      }));
      
      const quotes = (quotesRes.data || []).map(quote => ({
        ...quote,
        type: 'quote',
        document_number: quote.quote_number,
      }));

      return [...invoices, ...quotes];
    },
    enabled: !!profile?.id,
  });

  const handleSignDocument = (doc: any, type: 'invoice' | 'quote') => {
    setDocumentToSign(doc);
    setDocumentType(type);
    setShowSignature(true);
  };

  const handleDownloadDocument = (url: string) => {
    if (typeof window !== 'undefined') {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  const handleSignatureComplete = () => {
    setShowSignature(false);
    showSuccess('Document signed successfully!');
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Card>
          <CardContent className="p-6">
            <Skeleton className="h-4 w-48 mb-4" />
            <Skeleton className="h-20 w-full" />
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          Failed to load your documents. Please try again later.
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-6">
      {/* Pending Documents - Action Required */}
      {pendingDocs && pendingDocs.length > 0 && (
        <Card className="border-orange-200 bg-orange-50">
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <AlertCircle className="h-5 w-5 text-orange-600" />
              <span>Action Required - Documents Pending Signature</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {pendingDocs.map((doc) => (
                <div key={doc.id} className="flex items-center justify-between p-4 bg-white rounded-lg border">
                  <div className="flex items-center space-x-3">
                    <FileText className="h-5 w-5 text-orange-600" />
                    <div>
                      <p className="font-medium">
                        {doc.type === 'invoice' ? 'Invoice' : 'Quote'} #{doc.document_number}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {doc.type === 'invoice' ? 'Amount' : 'Total'}: £{doc.total_amount.toFixed(2)} • 
                        Sent: {format(new Date(doc.created_at), 'MMM dd, yyyy')}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Button
                      variant="default"
                      size="sm"
                      onClick={() => handleSignDocument(doc, doc.type)}
                    >
                      <Signature className="h-4 w-4 mr-1" />
                      Sign Now
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDownloadDocument(doc.signature_url || '#')}
                    >
                      <Eye className="h-4 w-4 mr-1" />
                      Preview
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Signed Documents */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <CheckCircle className="h-5 w-5 text-green-600" />
            <span>Signed Documents</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {signedDocs && signedDocs.length > 0 ? (
            <div className="space-y-4">
              {signedDocs.map((doc) => (
                <div key={doc.id} className="flex items-center justify-between p-4 bg-muted/30 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <CheckCircle className="h-5 w-5 text-green-600" />
                    <div>
                      <p className="font-medium">
                        {doc.type === 'invoice' ? 'Invoice' : 'Quote'} #{doc.document_number}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {doc.type === 'invoice' ? 'Amount' : 'Total'}: £{doc.total_amount.toFixed(2)} • 
                        Signed: {format(new Date(doc.signed_at), 'MMM dd, yyyy HH:mm')}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Badge variant="outline" className="bg-green-100 text-green-800">
                      <CheckCircle className="h-3 w-3 mr-1" />
                      Signed
                    </Badge>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDownloadDocument(doc.signature_url)}
                    >
                      <Download className="h-4 w-4 mr-1" />
                      Download
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <Alert>
              <FileText className="h-4 w-4" />
              <AlertDescription>
                No signed documents yet. Documents will appear here once you've signed them.
              </AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Document Signing Dialog */}
      <Dialog open={showSignature} onOpenChange={setShowSignature}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>
              Sign {documentType === 'invoice' ? 'Invoice' : 'Quote'} #{documentToSign?.document_number}
            </DialogTitle>
          </DialogHeader>
          {documentToSign && (
            <SignaturePad
              documentData={documentToSign}
              documentType={documentType}
              tableName={documentType === 'invoice' ? 'invoices' : 'quotes'}
              onSignComplete={handleSignatureComplete}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ClientSignedDocs;